/* eslint-disable import/no-cycle */

'use-strict';

import angular, { IScope } from 'angular';
import { TitleService } from 'src/app/shared/services/title';
import { NotificationService } from './shared/browser-functions/notification.service';
import { InterfaceService } from './shared/interface/interface.service';
import { LaunchService } from './components/app-launch/app-launch.service';
import { DateService } from './shared/misc/date.service';
import { LoginService } from './components/authorization/login.service';
import { ModalOverlayService } from './ui-components/modal-overlay/modal-overlay.service';
import { ToastService } from './ui-components/toast/toast.service';
import { NavService } from './ui-components/navigation-drawer/nav-drawer.service';
import { ConfigService } from './shared/config/config.service';
import { LoginResult } from './src/app/@types/login';
import { EssentialAppConfig } from './src/app/@shared-types/config';
import { ExternalCompany } from './src/app/@shared-types/user/external';
import { EExternalAPI } from './src/app/@shared-types/api';

export interface AutoboardNotification {
  id: number,
  title: string,
  read_status: boolean,
  link: string | null,
  link_text: string | null,
  text?: string,
}

export interface RootScope extends IScope {
  pageLoadComplete: boolean,
  loggedIn: boolean,
  onBoardingScreen: boolean,
  showChangelog: boolean,
  showOfflineBanner: boolean,
  navTitle: string,
  bodyOverFlowHide: boolean,
  showUpdateBanner: boolean,
  VERSION_NUMBER: string,
  BUILD_NUMBER: string,
  constants: { [key: string]: unknown },
  interface: InterfaceService,
  notificationService: NotificationService,
  modalOverlayService: ModalOverlayService,
  login: LoginService,
  dateService: DateService,
  toastService: ToastService,
  navService: NavService,
  TENANT_ID: string,
  frontpage: string,
  title: string,
  showNavigationDrawer: boolean,
  globals: {
    enabledApis: EExternalAPI[],
    [key: string]: unknown,
    config?: EssentialAppConfig,
    currentUser?: LoginResult['userDetails'],
    auth?: {
      token: string,
      authorizedUntil: Date,
    },
    deviceId?: string,
    externalCompanies?: ExternalCompany[],
  },
  configService: ConfigService,
  permissions?: {
    isAdmin: boolean,
    permissions?: { method: string, resource: string }[],
  },
  notifications: AutoboardNotification[],
  notificationToasts: AutoboardNotification[],
  notificationCount: number,
  notificationExpand: boolean,
  sidebarOpen: boolean,
  drawerUserSettings: boolean,
}

/* @ngInject */
export default function config(
  $rootScope: RootScope,
  interfaceService: InterfaceService,
  notificationService: NotificationService,
  constantsService: { [key: string]: unknown },
  launchService: LaunchService,
  dateService: DateService,
  loginService: LoginService,
  modalOverlayService: ModalOverlayService,
  toastService: ToastService,
  navService: NavService,
  $location: angular.ILocationService,
  configService: ConfigService,
  titleService: TitleService,
): void {
  /**
   * $rootScope Variables
   */
  $rootScope.constants = constantsService;
  $rootScope.interface = interfaceService;
  $rootScope.notificationService = notificationService;
  $rootScope.modalOverlayService = modalOverlayService;
  $rootScope.login = loginService;
  $rootScope.dateService = dateService;
  $rootScope.toastService = toastService;
  $rootScope.navService = navService;
  $rootScope.configService = configService;
  $rootScope.loggedIn = false;

  $rootScope.TENANT_ID = $location.host();
  if ($location.host() === '192.168.42.42') $rootScope.TENANT_ID = 'localhost';

  $rootScope.frontpage = '/';

  launchService.initializeAutoboard();

  /**
   * Change title each view change. Title is defined in route definition.
   */
  ($rootScope as angular.IRootScopeService).$on('$routeChangeSuccess', (_, current) => {
    titleService.setTitle(`${current.$$route.title} – Autoboard`);
  });

  /**
   * Hide the side menu after each location change.
   */
  $rootScope.$on('$locationChangeStart', () => {
    // If password change is forced by server, redirect to corresponding component
    if ($rootScope.globals.forcePasswordChange) {
      $location.path('/password-change');
    }

    navService.hide();
    interfaceService.toggleBodyOverflow(false);
  });

  $rootScope.$on('pushNotification', () => notificationService.getLatestNotifications());
}
